"use client";
import React from "react";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Loader2, Send, Plus, X } from "lucide-react";
import { useBookingForm } from "./booking/useBookingForm";
import {
  PatientDetailsFields,
  ServiceAndContactFields,
  AppointmentFields,
} from "./booking/FormFields";
import { Booking } from "@/lib/types";

interface BookingFormProps {
  defaultService?: string;
  isManual?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  // Kept for backward compatibility with the dashboard's existing
  // "Book Follow Up" entry point — see useBookingForm for details.
  initialData?: Booking | null;
}

/**
 * BookingForm Component
 * The main form for booking appointments: Name, Age, Gender, Phone, Date,
 * Time (all required) + Service (optional).
 */
export function BookingForm({
  defaultService,
  isManual,
  onSuccess,
  onCancel,
  initialData,
}: BookingFormProps) {
  const {
    form,
    isSubmitting,
    slotCounts,
    isLoadingSlots,
    selectedDate,
    setSelectedDate,
    blockedRanges,
    onSubmit,
  } = useBookingForm({ defaultService, isManual, onSuccess, initialData });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="w-full space-y-4">
        <PatientDetailsFields />
        <ServiceAndContactFields />
        <AppointmentFields
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          slotCounts={slotCounts}
          isLoadingSlots={isLoadingSlots}
          blockedRanges={blockedRanges}
        />
        <div className="flex gap-3 pt-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            className={onCancel ? "flex-1" : "w-full"}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isManual ? "Adding..." : "Submitting..."}
              </>
            ) : (
              <>
                {isManual ? (
                  <Plus className="mr-2 h-4 w-4" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                {isManual ? "Add Booking" : "Book Appointment"}
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
